import { PropsWithChildren, createContext, useContext, useMemo } from "react"
import { DocumentNode } from "@apollo/client"
import { getOperationName } from "@apollo/client/utilities"
import { groupBy } from "lodash-es"

type MutationQueryPair = {
  mutation: DocumentNode
  queries: DocumentNode[]
}

export const buildRefetchOperation = (
  mutation: DocumentNode,
  queries: DocumentNode[],
): MutationQueryPair => ({
  mutation,
  queries,
})

type RefetchContextType = {
  /**
   * The operations pair of mutation and query
   * @type {MutationQueryPair[]}
   * @example
   * buildRefetchOperation(MY_MUTATION, [MY_QUERY])
   */
  operations: MutationQueryPair[]
}

export const RefetchContext = createContext<RefetchContextType>({
  operations: [],
})
const useInternalRefetchContext = () => useContext(RefetchContext)

export const useRefetchManager = (mutation: DocumentNode) => {
  const { operations } = useInternalRefetchContext()
  const operation = useMemo(
    () => operations.find(op => getOperationName(op.mutation) === getOperationName(mutation)),
    [operations, mutation],
  )

  if (!operation) {
    throw new Error(
      `You must wrap your component in a Unstable_RefetchManagerProvider and set the queries to refetch for the mutation ${getOperationName(
        mutation,
      )}\n\n
Example:\n\n
<Unstable_RefetchManagerProvider operations={[buildRefetchOperation(MY_MUTATION, [MY_QUERY])]}>\n
  <MyComponent />\n
</Unstable_RefetchManagerProvider>\n
      `,
    )
  }

  return {
    queries: operation?.queries || [],
  }
}

/**
 * Provides a context for refetching queries in Apollo Client.
 *
 * @param {Object} props - The props for the context provider.
 * @param {ReactNode} props.children - The child components to render.
 * @param {MutationQueryPair[]} [props.operations=[]] - The operations pairs to refetch, each containing a mutation and an array of queries.
 * @returns {ReactNode} The context provider component.
 *
 * @example
 * <Unstable_RefetchManagerProvider operations={[buildRefetchOperation(MY_MUTATION, [MY_QUERY])]}>
 *   <MyComponent />
 * </Unstable_RefetchManagerProvider>
 *
 * @description
 * The `Unstable_RefetchManagerProvider` component provides a context for refetching queries in Apollo Client.
 * It accepts an `operations` prop, which is an array of `MutationQueryPair` objects representing the pairs of mutation and queries to refetch.
 * The providers can be nested, and the `operations` array will be merged together for the child provider.
 *
 * @example
 * // MyComponent.tsx
 * import { useRefetchManager } from "~/shared/contexts/RefetchContext"
 * const MyComponent = () => {
 *  const { queries } = useRefetchManager(MY_MUTATION)
 *  const [mutate] = useMutation(MY_MUTATION, {
 *     refetchQueries: queries
 *  })
 *  return (...);
 * }
 *
 * // ParentComponentOne.tsx
 * const ParentComponentOne = () => {
 *  return (
 *   <Unstable_RefetchManagerProvider operations={[buildRefetchOperation(MY_MUTATION, [MY_QUERY])]}>
 *    <MyComponent />
 *  </Unstable_RefetchManagerProvider>
 *  )
 * }
 *
 * // ParentComponentTwo.tsx
 * const ParentComponentTwo = () => {
 *  return (
 *   <Unstable_RefetchManagerProvider operations={[buildRefetchOperation(MY_MUTATION, [MY_OTHER_QUERY])]}>
 *    <MyComponent />
 *  </Unstable_RefetchManagerProvider>
 *  )
 * }
 *
 *
 * You can also use the `createRefetchManager` helper to create a provider with the given operations.
 * @example
 * const RefetchManager = createRefetchManager([buildRefetchOperation(MY_MUTATION, [MY_QUERY])])
 * const ParentComponent = () =>
 *  <RefetchManager>
 *    <MyComponent />
 *  </RefetchManager>
 */
export const Unstable_RefetchManagerProvider = ({
  children,
  operations = [],
}: PropsWithChildren<{ operations: MutationQueryPair[] }>) => {
  const { operations: parentOperations } = useInternalRefetchContext()

  const grouppedMutations = groupBy([...parentOperations, ...operations], ({ mutation }) =>
    getOperationName(mutation),
  )
  const mergedOperations = Object.values(grouppedMutations).map(operations => {
    return {
      mutation: operations[0].mutation,
      queries: operations.flatMap(({ queries }) => queries),
    }
  })

  return (
    <RefetchContext.Provider value={{ operations: mergedOperations }}>
      {children}
    </RefetchContext.Provider>
  )
}

/**
 * Creates a Unstable_RefetchManagerProvider component with the given operations.
 * This is just a helper to avoid having a big JSX footprint
 * @param {MutationQueryPair[]} operations - The operations pairs to refetch, each containing a mutation and an array of queries.
 * @returns {ReactNode} The context provider component.
 * @example
 * const RefetchManager = unstable_createRefetchManager([buildRefetchOperation(MY_MUTATION, [MY_QUERY])])
 */
export const unstable_createRefetchManager = (operations: MutationQueryPair[]) => {
  return ({ children }: PropsWithChildren<unknown>) => (
    <Unstable_RefetchManagerProvider operations={operations}>
      {children}
    </Unstable_RefetchManagerProvider>
  )
}
